"use client";

import { ArrowRight, Check } from "lucide-react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
} from "@/components/ui/motion";

const plans = [
  {
    name: "Free",
    price: "$0",
    description: "For side projects",
    features: ["1,000 runs/month", "5 workflows", "Community support"],
    cta: "Start free",
    popular: false,
  },
  {
    name: "Pro",
    price: "$29",
    period: "/month",
    description: "For growing teams",
    features: ["50,000 runs/month", "Unlimited workflows", "Priority support"],
    cta: "Start free trial",
    popular: true,
  },
  {
    name: "Enterprise",
    price: "$49",
    period: "/month",
    description: "For large organizations",
    features: ["Unlimited runs", "SSO & SAML", "Dedicated support"],
    cta: "Start free trial",
    popular: false,
  },
];

export function PricingTeaser() {
  const navigate = useRouter();

  return (
    <section
      id="pricing"
      className="py-20 lg:py-28 section-base relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <FadeInUp className="max-w-2xl mx-auto text-center mb-12">
          <h2 className="text-foreground mb-4">Simple, transparent pricing</h2>
          <p className="text-lg text-muted-foreground">
            Start free, scale as you grow. No hidden fees.
          </p>
        </FadeInUp>

        <StaggerContainer className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
          {plans.map((plan) => (
            <StaggerItem key={plan.name}>
              <div
                className={`relative p-6 rounded-xl border transition-all h-full flex flex-col ${
                  plan.popular
                    ? "bg-card border-primary/50 shadow-lg shadow-primary/10"
                    : "bg-card/50 border-border hover:border-muted-foreground/30"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 text-xs font-semibold bg-primary text-primary-foreground rounded-full">
                      Most popular
                    </span>
                  </div>
                )}

                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-foreground">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {plan.description}
                  </p>
                </div>

                <div className="mb-4">
                  <span className="text-3xl font-bold text-foreground">
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span className="text-muted-foreground">{plan.period}</span>
                  )}
                </div>

                <ul className="space-y-2 mb-6 flex-grow">
                  {plan.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-center gap-2 text-sm text-muted-foreground"
                    >
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button
                  variant={plan.popular ? "default" : "outline"}
                  className="w-full"
                  onClick={() => navigate.push("/login")}
                >
                  {plan.cta}
                </Button>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>

        <FadeInUp
          delay={0.3}
          className="text-center flex flex-col items-center gap-4"
        >
          <Button
            variant="ghost"
            onClick={() => navigate.push("/pricing")}
            className="text-primary"
          >
            See full pricing details
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          <button
            onClick={() => navigate.push("/pricing")}
            className="text-xs text-muted-foreground hover:text-primary transition-colors cursor-pointer"
          >
            Love what we're building? Gift a subscription →
          </button>
        </FadeInUp>
      </div>
    </section>
  );
}
